import { useEffect, useState } from "react";

/**
 * A porta do painel.
 *
 * Fica fora do `useLive` de propósito: aquele hook abre WebSocket e assume
 * sessão válida. Aqui ainda não há sessão — e um erro de rede na tela de login
 * precisa aparecer como texto, não como reconexão silenciosa.
 */
export function Login({ onEntrou }: { onEntrou: () => void }) {
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [entrando, setEntrando] = useState(false);
  const [semUsuario, setSemUsuario] = useState(false);

  useEffect(() => {
    // Instalação nova não tem conta nenhuma. Sem este aviso, a tela só diria
    // "senha inválida" para quem ainda não criou a primeira — e a pessoa
    // ficaria tentando adivinhar uma senha que não existe.
    fetch("/api/auth/estado")
      .then((r) => r.json())
      .then((d: { precisaCriarPrimeiro?: boolean }) =>
        setSemUsuario(Boolean(d.precisaCriarPrimeiro)),
      )
      .catch(() => undefined);
  }, []);

  async function entrar(ev: React.FormEvent) {
    ev.preventDefault();
    setEntrando(true);
    setErro(null);
    try {
      const r = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, senha }),
      });
      const d = (await r.json()) as { error?: string };
      if (!r.ok) {
        setErro(d.error ?? "não deu pra entrar");
        return;
      }
      onEntrou();
    } catch {
      setErro("servidor fora do ar");
    } finally {
      setEntrando(false);
    }
  }

  const campo = "w-full h-10 px-3.5 text-sm";

  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center text-center mb-8">
          <span className="w-12 h-12 rounded-2xl bg-gradient-to-br from-accent to-[#1f8f88] grid place-items-center text-ground font-bold text-xl shadow-[0_8px_24px_-6px_rgb(63_203_193/0.6)]">
            A
          </span>
          <h1 className="text-2xl font-semibold tracking-tight mt-5">Entrar no Axis</h1>
          <p className="text-sm text-ink-3 mt-1.5">Painel de atendimento</p>
        </div>

        <form
          onSubmit={entrar}
          className="space-y-4 p-6 rounded-2xl border border-rule bg-surface/80 backdrop-blur shadow-[var(--shadow-pop)]"
        >
          {semUsuario && (
            <div className="p-3 rounded-lg border border-warm/40 bg-warm-dim text-xs text-warm leading-relaxed">
              Ainda não há nenhum usuário cadastrado. No servidor, rode:
              <code className="block mt-1.5 text-ink-2 font-mono text-xs">
                pnpm usuario seu@email.com "Seu Nome"
              </code>
            </div>
          )}

          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-ink-2">E-mail</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              autoFocus
              required
              placeholder="voce@imobiliaria.com.br"
              className={campo}
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-ink-2">Senha</span>
            <input
              type="password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              autoComplete="current-password"
              required
              className={campo}
            />
          </label>

          {erro && (
            <p className="text-sm text-danger bg-danger-dim border border-danger/30 rounded-lg px-3 py-2">
              {erro}
            </p>
          )}

          <button
            type="submit"
            disabled={entrando}
            className="w-full h-10 bg-accent text-ground rounded-lg text-sm font-semibold
              shadow-[0_4px_14px_-4px_rgb(63_203_193/0.5)] hover:brightness-110
              active:scale-[0.99] transition-all disabled:opacity-50"
          >
            {entrando ? "Entrando…" : "Entrar"}
          </button>
        </form>
      </div>
    </div>
  );
}
